import { useEffect, useState } from "react";
import { MdOutlineEmail } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ForgotPasswordUserDataProps } from "..";
import { SignInValidationMessage } from "../../components/SignInValidationMessage";
import { Input } from "../../components/Input";
import { SignInButton } from "../../components/SignInButton";

import { wrapperRequests } from "../../../../services/api";

import { routesURL } from "../../../../services/routesUrl";
import * as S from "./styles";
import { CountrySelector } from "../../../../components/CountrySelector";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { AxiosError } from "axios";
import { toastify } from "../../../../shared/Toastify";

export function CheckEmail({ initialEmail }: ForgotPasswordUserDataProps) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const schema = z.object({
    countryCode: z.string().min(1, { message: t("countryRequired") }),
    email: z.string().email(t("invalidEmail")),
  });

  type CheckEmailSchema = z.infer<typeof schema>;

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<CheckEmailSchema>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (initialEmail) {
      setValue("email", initialEmail);
    }
  }, [initialEmail]);

  async function handleCheckEmail(data: CheckEmailSchema) {
    setIsLoading(true);
    setApiError("");

    const { email, countryCode } = data;

    try {
      await wrapperRequests(
        routesURL.auth.passwordRecoverValidateEmail,
        "POST",
        {
          data: {
            email,
            countryCode,
          },
        },
      );

      navigate(
        `/forgot-password/email-sent?email=${email}&countryCode=${countryCode}`,
      );
    } catch (error: unknown) {
      setIsLoading(false);
      if (error instanceof AxiosError) {
        toastify(
          "error",
          error.response?.data.message || t("errorDuringRecoveryProcess"),
          "top-right",
        );
      }

      throw Error(String(error));
    }
  }

  return (
    <S.CheckEmailContainer>
      <S.Description>{t("passwordRecoveryDescription")}</S.Description>

      <CountrySelector
        error={errors.countryCode?.message}
        {...register("countryCode")}
        onCountryChange={(countryCode) => {
          setValue("countryCode", countryCode, { shouldValidate: true });
        }}
      />

      <Input
        label={t("email")}
        type="email"
        icon={<MdOutlineEmail />}
        errorMessage={errors.email?.message}
        {...register("email")}
      />

      <SignInButton
        type="submit"
        isLoading={isLoading}
        onClick={handleSubmit(handleCheckEmail)}
        title={t("recoverPassword")}
      />

      {apiError && <SignInValidationMessage message={apiError} />}
    </S.CheckEmailContainer>
  );
}
